import {useCallback, useContext, useEffect, useState} from 'react';


import {MessageContext} from 'src/contexts/message-context';
import {getConnectionErrorMessage} from 'src/modules/profile/account/tabs/license-and-features/crm-tab/utils/getDuplicatesErrorMessage';
import {
    checkMsDynamicCredentials,
    cleanupMsDynamicOAuthCredentials,
    getMsDynamicCredentials,
    saveMsDynamicCredentials,
    unlinkMsDynamicOrgId,
} from 'src/openapi/admin/apis/MsDynamicsControllerApi';
import {CredentialsType as CredentialsTypes} from 'src/openapi/admin/models/CredentialsType';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {checkIsChanged} from 'src/utils/use-case-utils';

import {isMsDynamicTestReady} from './utils/isMsDynamicTestReady';

import type {CredentialsUpdateResult} from 'src/openapi/admin/models/CredentialsUpdateResult';
import type {Error} from 'src/openapi/admin/models/Error';
import type {MsDynamicCredentials} from 'src/openapi/admin/models/MsDynamicCredentials';

const DEFAULT_MS_DYNAMICS_CREDENTIALS: MsDynamicCredentials = {
    passwordCredentials: {login: '', password: '', passwordChanged: false, passwordNotEmpty: false},
    credentialsType: CredentialsTypes.PASSWORD,
    tokenCacheNotEmpty: false,
    url: '',
    crmUrl: '',
};

type Props = {
    accountId: number,
    isCrmEnabled: boolean,
};

export const useMSDynamicsCredentials = (props: Props) => {
    const {accountId, isCrmEnabled} = props;
    const apiDispatch = useAppDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    const [isLoading, setIsLoading] = useState(true);
    const [isConnectionStatusLoading, setIsConnectionStatusLoading] = useState(false);
    const [isUnlinkBusy, setUnlinkBusy] = useState(false);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const [initialData, setInitialData] = useState<MsDynamicCredentials | null>(null);
    const [data, setData] = useState<MsDynamicCredentials>(DEFAULT_MS_DYNAMICS_CREDENTIALS);
    const [connectionStatus, setConnectionStatus] = useState<CredentialsUpdateResult | null>(null);

    const testConnection = useCallback(async (msDynamicCredentials: MsDynamicCredentials) => {
        setIsConnectionStatusLoading(true);
        const testCredentialsResponse = await apiDispatch(checkMsDynamicCredentials({
            accountId,
            msDynamicCredentials,
        }));

        if (testCredentialsResponse.payload.success) {
            setConnectionStatus(testCredentialsResponse.payload.data);
        } else {
            setErrorMessage(getError({error: testCredentialsResponse.payload.data}));
        }
        setIsConnectionStatusLoading(false);
    }, [accountId, apiDispatch, setErrorMessage]);

    const discardChanges = useCallback(() => {
        if (initialData) {
            setData(initialData);
            testConnection(initialData);
        }
    }, [initialData, testConnection]);

    useEffect(() => {
        const loadMsDynamicCredentials = async () => {
            setIsLoading(true);
            const msDynamicCredentialsResponse = await apiDispatch(getMsDynamicCredentials({accountId}));
            if (msDynamicCredentialsResponse.payload.success) {
                setInitialData(msDynamicCredentialsResponse.payload.data);
                setData(msDynamicCredentialsResponse.payload.data);

                if (isMsDynamicTestReady(msDynamicCredentialsResponse.payload.data) && isCrmEnabled) {
                    testConnection(msDynamicCredentialsResponse.payload.data);
                }
            } else {
                setLoadingError(msDynamicCredentialsResponse.payload.data);
            }
            setIsLoading(false);
        };

        loadMsDynamicCredentials();
    }, [accountId, isCrmEnabled, apiDispatch, testConnection]);

    const saveChanges = useCallback(async () => {
        let error: string | null = null;

        const msDynamicCredentialsResponse = await apiDispatch(saveMsDynamicCredentials({
            accountId,
            msDynamicCredentials: data,
        }));

        if (msDynamicCredentialsResponse.payload.error) {
            error = getError({error: msDynamicCredentialsResponse.payload.data, separator: ' '});
        } else if (msDynamicCredentialsResponse.payload.data.type?.code === 'dup') {
            error = getConnectionErrorMessage(msDynamicCredentialsResponse.payload.data);
        } else {
            setInitialData(data);
            if (isMsDynamicTestReady(data)) {
                setConnectionStatus(msDynamicCredentialsResponse.payload.data);
            } else {
                setConnectionStatus(null);
            }
        }

        return error;
    }, [accountId, data, apiDispatch]);

    const unlinkConnection = useCallback(async () => {
        setUnlinkBusy(true);
        const msDynamicsUnlinkResponse = await apiDispatch(unlinkMsDynamicOrgId({accountId}));
        if (msDynamicsUnlinkResponse.payload.success) {
            setConnectionStatus(null);
        } else {
            setErrorMessage(getError({error: msDynamicsUnlinkResponse.payload.data}));
        }
        setUnlinkBusy(false);
    }, [accountId, apiDispatch, setErrorMessage]);

    const revokeConnection = useCallback(async () => {
        const msDynamicsRevokeResponse = await apiDispatch(
            cleanupMsDynamicOAuthCredentials({accountId}),
        );
        if (msDynamicsRevokeResponse.payload.success) {
            setConnectionStatus(null);
        } else {
            setErrorMessage(getError({error: msDynamicsRevokeResponse.payload.data}));
        }
    }, [accountId, apiDispatch, setErrorMessage]);

    return {
        isLoading,
        isConnectionStatusLoading,
        isUnlinkBusy,
        loadingError,
        isChanged: checkIsChanged(initialData, data),

        data, setData,
        connectionStatus,

        saveChanges,
        unlinkConnection,
        revokeConnection,
        testConnection,
        discardChanges,
    };
};
